// src/pages/WeddingPage.jsx
import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  FaShoppingCart,
  FaBars,
  FaTimes,
  FaHome,
  FaChevronDown,
  FaUserCircle,
} from "react-icons/fa";
import { useCart } from "../cart/CartContext";
import { useAuth } from "../auth/AuthContext";
import "../styles/WeddingPage.css";

import heroImg from "../assets/venue.jpg";

const servicesLinks = [
  { to: "/wedding", label: "Wedding Planning" },
  { to: "/design", label: "Design & Decoration" },
  { to: "/destination", label: "Destination Wedding Venues" },
  { to: "/entertainment", label: "Entertainment" },
  { to: "/others", label: "Custom Events" },
];

function WeddingPage() {
  const { cart, addToCart } = useCart();
  const { user } = useAuth();
  const [dropdown, setDropdown] = useState(false);
  const [navOpen, setNavOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [addedId, setAddedId] = useState(null);
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const dropdownTimeout = useRef(null);
  const navigate = useNavigate();

  const handleProfileClick = (e) => {
    e.preventDefault();
    if (!user) {
      navigate("/login");
    } else {
      navigate("/profile");
    }
  };
  const packagesRef = useRef(null);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Load from CMS JSON
  useEffect(() => {
    (async () => {
      try {
        const res = await fetch("/content/wedding.json", { cache: "no-store" });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        setContent(data);
      } catch (e) {
        console.error(e);
        setErr("Failed to load wedding content.");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const toggleNav = () => {
    setNavOpen(!navOpen);
    document.body.classList.toggle("nav-open", !navOpen);
  };

  const handleAdd = (pkg) => {
    if (!user) {
      alert("Please login to add items to cart");
      navigate("/");
      return;
    }
    addToCart({
      id: pkg.id,
      name: pkg.name,
      description: pkg.description,
      image: pkg.image,
      price: typeof pkg.price === "number" ? pkg.price : 0, // safety
    });
    setAddedId(pkg.id);
    setTimeout(() => setAddedId(null), 1000);
  };

  const scrollToPackages = () => {
    packagesRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  if (loading) {
    return <div className="checking-screen">Loading wedding content…</div>;
  }
  if (err) {
    return <div className="checking-screen">{err}</div>;
  }

  const heroTitle = content?.hero?.title || "Your Dream Wedding Awaits";
  const heroSubtitle = content?.hero?.subtitle || "Elegant • Memorable • Completely Yours";
  const packages = content?.packages || [];

  return (
    <div className="wedding-page">
      {/* NAVBAR */}
      <header className={`desktop-navbar ${scrolled ? "scrolled" : ""}`}>
        <div className="navbar-container">
          <div
            className="navbar-brand"
            onClick={() => navigate("/")}
            style={{ fontFamily: "'Great Vibes', cursive" }}
          >
            Parivar
          </div>

          <nav className="center-nav">
            <Link to="/" className="nav-link">
              <FaHome /> Home
            </Link>

            <div
              className="dropdown"
              onMouseEnter={() => {
                clearTimeout(dropdownTimeout.current);
                setDropdown(true);
              }}
              onMouseLeave={() => {
                dropdownTimeout.current = setTimeout(() => setDropdown(false), 200);
              }}
            >
              <button className="dropdown-btn">
                Services <FaChevronDown />
              </button>
              <div className={`dropdown-menu ${dropdown ? "show" : ""}`}>
                {servicesLinks.map((item) => (
                  <Link
                    key={item.to}
                    to={item.to}
                    className="dropdown-link"
                    onClick={() => setDropdown(false)}
                  >
                    {item.label}
                  </Link>
                ))}
              </div>
            </div>
          </nav>

          {/* RIGHT ICONS */}
          <div className="nav-right">
            <a href="/profile" onClick={handleProfileClick} className="cart-link" aria-label="Profile">
              <FaUserCircle size={18} />
            </a>
            <Link to="/cart" className="cart-link" aria-label="Cart">
              <FaShoppingCart size={18} />
              {cart.length > 0 && <span className="cart-badge">{cart.length}</span>}
            </Link>
          </div>

          <div className="mobile-menu-icon" onClick={toggleNav}>
            {navOpen ? <FaTimes size={22} /> : <FaBars size={22} />}
          </div>
        </div>
      </header>

      {/* MOBILE MENU */}
      {navOpen && (
        <div className="sidebar-overlay" onClick={toggleNav}>
          <div className="sidebar" onClick={(e) => e.stopPropagation()}>
            <div className="sidebar-header">
              <span>Menu</span>
              <FaTimes onClick={toggleNav} />
            </div>

            <div className="sidebar-link" onClick={() => { navigate("/"); toggleNav(); }}>
              <FaHome /> Home
            </div>

            <div className="sidebar-link" onClick={() => setDropdown(!dropdown)}>
              <FaChevronDown /> Services
            </div>
            {dropdown && (
              <div className="sidebar-submenu">
                {servicesLinks.map((link) => (
                  <div
                    key={link.to}
                    className="sidebar-submenu-item"
                    onClick={() => { navigate(link.to); toggleNav(); }}
                  >
                    {link.label}
                  </div>
                ))}
              </div>
            )}

            <div className="sidebar-link" onClick={() => { 
              if (!user) {
                navigate("/login");
              } else {
                navigate("/profile");
              }
              toggleNav(); 
            }}>
              <FaUserCircle /> Profile
            </div>

            <div className="sidebar-link" onClick={() => { navigate("/cart"); toggleNav(); }}>
              <FaShoppingCart /> Cart
              {cart.length > 0 && <span className="cart-badge">{cart.length}</span>}
            </div>
          </div>
        </div>
      )}

      {/* HERO (title/subtitle from CMS; background stays local) */}
      <section className="hero" style={{ backgroundImage: `url(${heroImg})` }}>
        <div className="hero-overlay" />
        <div className="hero-content">
          <h1>{heroTitle}</h1>
          <p>{heroSubtitle}</p>
          <button onClick={scrollToPackages} className="hero-btn">Start Planning</button>
        </div>
      </section>

      {/* PACKAGES */}
      <section ref={packagesRef} className="package-section">
        <h2>Our Wedding Packages</h2>

        {packages.length === 0 && (
          <div className="no-results">No packages available right now. Please check back soon.</div>
        )}

        <div className="packages-grid">
          {packages.map((pkg) => {
            const isAvailable = pkg.status !== "unavailable";
            const hasNumericPrice = typeof pkg.price === "number" && !isNaN(pkg.price);
            const priceDisplay = pkg?.price_label?.trim()
              ? pkg.price_label.trim()
              : hasNumericPrice
              ? `₹${pkg.price.toLocaleString()}`
              : "—";

            return (
<div
  key={pkg.id}
  className={`package-card ${addedId === pkg.id ? "added" : ""} ${!isAvailable ? "unavailable" : ""}`}
>
  {/* removed the corner badge */}

  <img src={pkg.image} alt={pkg.name} className="package-img" />
  <div className="package-info">
    <h3>
      {pkg.name}{" "}
      <span className={`pkg-status ${isAvailable ? "available" : "unavailable"}`}>
        {isAvailable ? "Available" : "Unavailable"}
      </span>
    </h3>

    <p>{pkg.description}</p>

    {Array.isArray(pkg.features) && pkg.features.length > 0 && (
      <ul>
        {pkg.features.map((f, i) => (
          <li key={i}>• {typeof f === "string" ? f : f?.feature}</li>
        ))}
      </ul>
    )}

    <div className="card-footer">
      <span className="price">{priceDisplay}</span>

      {isAvailable && !hasNumericPrice && pkg?.price_label ? (
        <button
          onClick={() => navigate("/others")}
          className="add-btn outline"
          title="Request a custom quote"
        >
          Request Quote
        </button>
      ) : (
        <button
          onClick={() => isAvailable && hasNumericPrice && handleAdd(pkg)}
          className={`add-btn ${addedId === pkg.id ? "added" : ""}`}
          disabled={!isAvailable || addedId === pkg.id || !hasNumericPrice}
          title={!isAvailable ? "Unavailable" : !hasNumericPrice ? "Price not set" : "Add to cart"}
        >
          {!isAvailable
            ? "Unavailable"
            : hasNumericPrice
            ? addedId === pkg.id
              ? "✔ Added"
              : "Add to Cart"
            : "Not Available"}
        </button>
      )}
    </div>
  </div>
</div>

            );
          })}
        </div>
      </section>

      {/* CTA */}
      <section className="cta-section">
        <h2>Ready to Make Your Wedding Unforgettable?</h2>
        <button onClick={scrollToPackages} className="cta-btn">
          Get Started
        </button>
      </section>
    </div>
  );
}

export default WeddingPage;
